import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table'
import type { Composer } from '@brent/shared/types'
import { useAuth } from '@/contexts/AuthContext'
import { useUpdateComposer } from '@/hooks/useComposers'
import { useToast } from '@/hooks/use-toast'
import { isFlagged, isEffectivelyMatched } from '@/lib/confidence'
import { ConfidenceBadge } from '@/components/shared/ConfidenceBadge'
import { QualityDropdown } from '@/components/shared/QualityDropdown'
import { WikidataLink, WikidataSearchLink } from '@/components/shared/WikidataLink'
import { InlineEdit } from '@/components/shared/InlineEdit'
import { Button } from '@brent/core-ui/ui'
import { Input } from '@brent/core-ui/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@brent/core-ui/ui'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@brent/core-ui/ui'
import { ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react'
import type { QualityScore } from '@brent/shared/types'

export type ComposerFilter =
  | 'all'
  | 'matched'
  | 'unmatched'
  | 'flagged'
  | 'verified'
  | 'needs_review'

interface ComposersTableProps {
  composers: Composer[]
  filter: ComposerFilter
  onFilterChange: (filter: ComposerFilter) => void
}

function formatDates(birthYear: number | null, deathYear: number | null): string {
  if (!birthYear && !deathYear) return '-'
  const b = birthYear || '?'
  const d = deathYear || '?'
  return `${b}\u2013${d}`
}

export function ComposersTable({ composers, filter, onFilterChange }: ComposersTableProps) {
  const navigate = useNavigate()
  const { canEdit } = useAuth()
  const updateComposer = useUpdateComposer()
  const { toast } = useToast()
  const [sorting, setSorting] = useState<SortingState>([{ id: 'programme_count', desc: true }])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const filteredComposers = useMemo(
    () =>
      composers.filter((c) => {
        switch (filter) {
          case 'matched':
            return isEffectivelyMatched(c)
          case 'unmatched':
            return !isEffectivelyMatched(c)
          case 'flagged':
            return isFlagged(c.name_canonical, c.wikidata_label, c.confidence, c.quality_score)
          case 'verified':
            return c.quality_score === 'verified'
          case 'needs_review':
            return c.quality_score === 'needs_review'
          default:
            return true
        }
      }),
    [composers, filter],
  )

  const handleQualityChange = useCallback(
    async (composer: Composer, qualityScore: QualityScore) => {
      try {
        await updateComposer.mutateAsync({
          id: composer.id,
          input: { quality_score: qualityScore },
        })
        toast({ title: 'Updated', description: `Quality set to ${qualityScore}` })
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to update quality',
        })
      }
    },
    [updateComposer, toast],
  )

  const handleQidSave = useCallback(
    async (composer: Composer, qid: string) => {
      try {
        const result = await updateComposer.mutateAsync({
          id: composer.id,
          input: { wikidata_id: qid },
        })
        if (result.enriched) {
          toast({
            title: 'Enriched',
            description: `Matched to ${result.enriched.label}`,
          })
        } else if (result.cleared) {
          toast({ title: 'Cleared', description: 'Wikidata ID removed' })
        }
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to update QID',
        })
      }
    },
    [updateComposer, toast],
  )

  const columns: ColumnDef<Composer>[] = useMemo(
    () => [
      {
        accessorKey: 'name_canonical',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => <div className="font-medium">{row.getValue('name_canonical')}</div>,
      },
      {
        accessorKey: 'wikidata_id',
        header: 'QID',
        cell: ({ row }) => {
          const composer = row.original
          if (canEdit) {
            return (
              <InlineEdit
                value={composer.wikidata_id || ''}
                onSave={(qid) => handleQidSave(composer, qid)}
                placeholder="Add QID"
              />
            )
          }
          return <WikidataLink qid={composer.wikidata_id} />
        },
      },
      {
        accessorKey: 'wikidata_label',
        header: 'Wikidata Label',
        cell: ({ row }) => {
          const composer = row.original
          if (composer.wikidata_label) {
            return <WikidataLink qid={composer.wikidata_id} label={composer.wikidata_label} />
          }
          if (composer.name_canonical && !composer.wikidata_id) {
            return <WikidataSearchLink text={composer.name_canonical} />
          }
          return <span className="text-muted-foreground">-</span>
        },
      },
      {
        accessorKey: 'confidence',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Confidence
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <ConfidenceBadge
            confidence={row.original.confidence}
            qualityScore={row.original.quality_score}
          />
        ),
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.confidence ?? -1
          const b = rowB.original.confidence ?? -1
          return a - b
        },
      },
      {
        id: 'dates',
        header: 'Dates',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {formatDates(row.original.birth_year, row.original.death_year)}
          </span>
        ),
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.birth_year ?? 0
          const b = rowB.original.birth_year ?? 0
          return a - b
        },
      },
      {
        accessorKey: 'programme_count',
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Items
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => <span className="tabular-nums">{row.getValue('programme_count')}</span>,
      },
      {
        accessorKey: 'quality_score',
        header: 'Status',
        cell: ({ row }) => {
          const composer = row.original
          if (canEdit) {
            return (
              <QualityDropdown
                value={composer.quality_score}
                onChange={(qs) => handleQualityChange(composer, qs)}
                disabled={updateComposer.isPending}
              />
            )
          }
          return (
            <span className="text-sm capitalize">{composer.quality_score.replace('_', ' ')}</span>
          )
        },
      },
    ],
    [canEdit, handleQidSave, handleQualityChange, updateComposer.isPending],
  )

  const table = useReactTable({
    data: filteredComposers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: { sorting, columnFilters },
    initialState: {
      pagination: { pageSize: 25 },
    },
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <Input
          placeholder="Search composers..."
          value={(table.getColumn('name_canonical')?.getFilterValue() as string) ?? ''}
          onChange={(e) => table.getColumn('name_canonical')?.setFilterValue(e.target.value)}
          className="max-w-sm"
        />
        <Select value={filter} onValueChange={(v) => onFilterChange(v as ComposerFilter)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="matched">Matched</SelectItem>
            <SelectItem value="unmatched">Unmatched</SelectItem>
            <SelectItem value="flagged">Flagged</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="needs_review">Needs Review</SelectItem>
          </SelectContent>
        </Select>
        <div className="sm:ml-auto text-sm text-muted-foreground">
          {table.getFilteredRowModel().rows.length} of {filteredComposers.length} composers
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/composers/${row.original.id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No composers found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={String(table.getState().pagination.pageSize)}
            onValueChange={(v) => table.setPageSize(Number(v))}
          >
            <SelectTrigger className="h-8 w-[80px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 25, 50, 100].map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
